import { Directive, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Digits Only Directive
 * Usage:
 * <input type="number" appDigitsOnly
 * [maxlength]="4"  //- sets the max length of the field (when used with decimals, it only limits the left part)
 * [defaultValue]="1"  //- sets a default value to be set when the field is left empty
 * [decimals]="2"  //- sets the number of decimals after the dot
 * [allowNegatives]="true"  //- allows negative numbers (should not be used with type="number")
 * (ngModelChange)="updateModel(value)"  //- Output method used with defaultValue to properly update ngModel if used
 * >
 */
@Directive({
  selector: '[appDigitsOnly]',
  standalone: false
})
export class DigitsOnlyDirective implements OnInit {
  private keys = {
    Delete: 'Delete',
    Backspace: 'Backspace',
    Tab: 'Tab',
    Esc: 'Escape',
    Enter: 'Enter',
    Left: 'ArrowLeft',
    Right: 'ArrowRight',
    Home: 'Home',
    End: 'End',
    Period: '.',
    Dash: '-'
  };
  private numKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  private oldVal = null;
  @Input() maxlength: number;
  @Input() defaultValue: number;
  @Input() decimals: number;
  @Input() allowNegatives: boolean;
  @Output() ngModelChange = new EventEmitter();
  maxAllowedDots: number;
  maxAllowedDash: number;
  private nativeEl: HTMLInputElement;
  private formControl: NgControl;
  numericRealRegex = /(?!^-)[^0-9.]/g;
  numericIntRegex = /[^0-9.]/g;

  constructor(private el: ElementRef, private control: NgControl) {
    this.nativeEl = this.el.nativeElement;
    this.formControl = this.control;
  }

  ngOnInit() {
    if (this.nativeEl.getAttribute('type') === 'number' && this.allowNegatives) {
      throw new Error('Allow negatives cannot be used with input type number');
    }

    setTimeout(() => {
      const elMaxLength = this.nativeEl.getAttribute('maxlength');
      if (!this.maxlength && Number(elMaxLength) > 0) {
        this.maxlength = Number(elMaxLength);
      }
      this.maxAllowedDots = this.decimals ? 1 : 0;
      this.maxAllowedDash = this.allowNegatives ? 1 : 0;
    });
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event) {
    const e = event as KeyboardEvent;

    // Allow: Delete, Backspace, Tab, Esc,Enter
    if (this.isSpecialKey(e) || this.isSpecialAction(e)) {
      // let it happen, don't do anything
      return;
    }

    // Ensure that it is a number and stop the keypress if not
    if (this.validateNumbers(e)) {
      e.preventDefault();
      return;
    }

    // Checks if there is a dot character and if it's bigger than the max allowed dots
    if ((this.nativeEl.value.match(/\./g) || []).length >= this.maxAllowedDots && e.key === this.keys.Period) {
      e.preventDefault();
      return;
    }

    // Checks if there is a dash character and if it's bigger than the max allowed dash
    if ((this.nativeEl.value.match(/-/g) || []).length >= this.maxAllowedDash && e.key === this.keys.Dash) {
      e.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  onInput(event) {
    // replace unwanted characters
    if (this.nativeEl.value) {
      const cleanValue = this.nativeEl.value.replace(this.allowNegatives ? this.numericRealRegex : this.numericIntRegex, '');
      this.updateValue(cleanValue, event);
    }

    // trim number of decimals
    if (!this.decimals) {
      const intValue = this.nativeEl.value.split('.')[0];
      const trimmedValue = intValue.slice(0, Number(this.maxlength));
      this.updateValue(trimmedValue, event);
      return;
    }

    // prevent pasting/entering wrong data
    if (this.oldVal && !this.nativeEl.value && !this.nativeEl.validity.valid && event.data) {
      this.updateValue(this.oldVal, event);
      return;
    }

    // trim decimals to desired value
    let parts = this.nativeEl.value.split('.');
    if (parts[0]) {
      parts[0] = parts[0].slice(0, Number(this.maxlength));
    }
    if (parts[1]) {
      parts[1] = parts[1].slice(0, Number(this.decimals));
    }
    if (parts.length > 2) {
      parts = parts.slice(0, 2);
    }
    this.updateValue(parts.join('.'), event);
  }

  private updateValue(newValue, event) {
    if (!this.nativeEl.value && !newValue) {
      // clear old value if we delete the value from the field
      if (!event.data) {
        this.oldVal = '';
      }
      return;
    }

    // update element value and old value
    if (this.nativeEl.value !== newValue) {
      this.nativeEl.value = newValue;
    }
    this.oldVal = newValue;

  }

  // used for defaultValue
  @HostListener('blur')
  onBlur() {
    if (this.nativeEl.getAttribute('type') === 'number') {
      this.formControl.control.setValue(Number(this.nativeEl.value));
    } else {
      this.formControl.control.setValue(this.nativeEl.value);
    }

    // set default value
    if (this.defaultValue && !Number(this.nativeEl.value)) {
      this.nativeEl.value = this.defaultValue.toString();
      this.ngModelChange.emit(this.nativeEl.value);
    }
  }

  isSpecialKey(e) {
    return [this.keys.Delete, this.keys.Backspace, this.keys.Tab, this.keys.Esc, this.keys.Enter].indexOf(e.key) !== -1;
  }

  isSpecialAction(e) {
    // Allow: Ctrl+A
    return (e.key === 'a' && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+C
      (e.key === 'c' && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+V
      (e.key === 'v' && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+X
      (e.key === 'x' && (e.ctrlKey || e.metaKey)) ||
      // Allow: home, end, left, right, NO up, down (because it increases, decreases numbers)
      (e.key === this.keys.Home || e.key === this.keys.End || e.key === this.keys.Left || e.key === this.keys.Right);
  }

  validateNumbers(e) {
    return this.numKeys.indexOf(e.key) === -1 && (!this.decimals || e.key !== this.keys.Period) &&
      (!this.allowNegatives || e.key !== this.keys.Dash);
  }
}
